import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { FaArrowRight, FaCheck, FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import styles from './LeadInquiryModal.module.css';

type Icon = React.FC<{ 'aria-hidden'?: string }>;
const ArrowIcon = FaArrowRight as unknown as Icon;
const CheckIcon = FaCheck as unknown as Icon;
const MapIcon = FaMapMarkerAlt as unknown as Icon;
const CloseIcon = FaTimes as unknown as Icon;

type InquiryFormData = {
  name: string;
  phone: string;
  service: string;
  location: string;
};

const initialForm: InquiryFormData = { name: '', phone: '', service: '', location: '' };

const LeadInquiryModal: React.FC = () => {
  const navigate = useNavigate();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
  const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
  const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';

  useEffect(() => {
    const updateVisibility = () => {
      const formIsOpen = window.location.hash === '#form';
      setIsOpen(formIsOpen);
      if (!formIsOpen) {
        setError('');
        setFormData(initialForm);
      }
    };

    updateVisibility();
    window.addEventListener('hashchange', updateVisibility);
    return () => window.removeEventListener('hashchange', updateVisibility);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && closeModal();
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const closeModal = () => {
    if (window.location.hash === '#form') {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!formData.name.trim() || !formData.phone.trim() || !formData.service.trim()) {
      setError('Моля, попълнете име, телефон и желаната услуга.');
      return;
    }

    setIsSubmitting(true);
    try {
      const templateParams = {
        from_name: formData.name,
        phone: formData.phone,
        service: formData.service,
        location: formData.location || '—',
        source: 'Geoworld landing page /#form',
      };

      if (serviceId && templateId && publicKey) {
        await emailjs.send(serviceId, templateId, templateParams, publicKey);
      } else {
        console.warn('EmailJS is not configured yet. Lead form was accepted locally.');
      }

      if (window.gtag) window.gtag('event', 'generate_lead', { event_category: 'lead_form', event_label: formData.service });
      if (window.fbq) window.fbq('track', 'Lead');
      navigate('/thank-you');
    } catch (submitError) {
      console.error('Lead form submission failed', submitError);
      setError('Не успяхме да изпратим заявката. Моля, опитайте отново или ни се обадете директно.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} role="presentation" onMouseDown={closeModal}>
      <section
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-inquiry-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button ref={closeButtonRef} type="button" className={styles.closeButton} onClick={closeModal} aria-label="Затвори формата">
          <CloseIcon aria-hidden="true" />
        </button>

        <aside className={styles.intro}>
          <p className={styles.eyebrow}>Безплатна консултация</p>
          <h2 id="lead-inquiry-title">Да обсъдим Вашия обект</h2>
          <p className={styles.introText}>
            Обадете ни се на <a href="tel:+359883354449">0883 35 44 49</a> или <a href="tel:+359899271495">0899 27 14 95</a>, или изпратете кратка заявка и ще Ви потърсим, за да уточним най-подходящото решение и срок.
          </p>
          <ul className={styles.benefits}>
            <li><CheckIcon aria-hidden="true" /> Професионален отговор</li>
            <li><CheckIcon aria-hidden="true" /> Ясни срокове и следващи стъпки</li>
            <li><CheckIcon aria-hidden="true" /> Обслужване в цяла България</li>
            <li><CheckIcon aria-hidden="true" /> Ще се свържем с Вас до 24 часа</li>
          </ul>
        </aside>

        <div className={styles.formArea}>
          <p className={styles.formLabel}>Попълнете данните си</p>
          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <label className={styles.field}>
              <span>Име и фамилия <em>*</em></span>
              <input name="name" value={formData.name} onChange={handleChange} placeholder="Иван Иванов" autoComplete="name" required />
            </label>
            <label className={styles.field}>
              <span>Телефон <em>*</em></span>
              <input name="phone" value={formData.phone} onChange={handleChange} placeholder="+359 88 123 4567" autoComplete="tel" inputMode="tel" required />
            </label>
            <label className={`${styles.field} ${styles.fullWidth}`}>
              <span>Каква услуга Ви е нужна? <em>*</em></span>
              <select name="service" value={formData.service} onChange={handleChange} required>
                <option value="" disabled>Изберете услуга</option>
                <option>Геодезическо заснемане</option>
                <option>Трасиране</option>
                <option>Кадастрални услуги</option>
                <option>Проектиране</option>
                <option>Друго</option>
              </select>
            </label>
            <label className={`${styles.field} ${styles.fullWidth}`}>
              <span><MapIcon aria-hidden="true" /> Къде е обектът?</span>
              <select name="location" value={formData.location} onChange={handleChange}>
                <option value="">Изберете област</option>
                <option>Благоевград</option>
                <option>Бургас</option>
                <option>Варна</option>
                <option>Велико Търново</option>
                <option>Видин</option>
                <option>Враца</option>
                <option>Габрово</option>
                <option>Добрич</option>
                <option>Кърджали</option>
                <option>Кюстендил</option>
                <option>Ловеч</option>
                <option>Монтана</option>
                <option>Пазарджик</option>
                <option>Перник</option>
                <option>Плевен</option>
                <option>Пловдив</option>
                <option>Разград</option>
                <option>Русе</option>
                <option>Силистра</option>
                <option>Сливен</option>
                <option>Смолян</option>
                <option>София-град</option>
                <option>Софийска област</option>
                <option>Стара Загора</option>
                <option>Търговище</option>
                <option>Хасково</option>
                <option>Шумен</option>
                <option>Ямбол</option>
              </select>
            </label>
            {error && <p className={styles.error} role="alert">{error}</p>}
            <button type="submit" className={styles.primaryButton} disabled={isSubmitting}>
              {isSubmitting ? 'Изпращане…' : <>Изпратете заявката <ArrowIcon aria-hidden="true" /></>}
            </button>
            <p className={styles.privacyNote}>С изпращането се съгласявате да се свържем с Вас по тази заявка.</p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default LeadInquiryModal;
